/* ═══════════════════════════════════════════════════════════════
   The Designer — the table context toolbar (contract §4 R7).

   Appears only while the selection sits inside a table, anchored
   under it (DESIGN §4: context UI always on top — portalled, fixed,
   viewport-clamped). Every button calls one of the framework's own
   table commands (addRowBefore, mergeCells, toggleHeaderRow, …) —
   this file supplies zero editing logic of its own, only the UI and
   the numbered/standalone + per-cell-style attrs the framework
   doesn't have an opinion about.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Columns3,
  PanelTop,
  Rows3,
  TableCellsMerge,
  TableCellsSplit,
  Trash2,
} from 'lucide-react';
import { TABLE_NODE_NAME } from './tables';

interface Props {
  editor: Editor;
  canEdit: boolean;
}

/** The table node the selection is currently inside, or null. Depth-walks
 *  the resolved position — the same technique BlockMenu/find use elsewhere. */
function tablePos(editor: Editor): number | null {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === TABLE_NODE_NAME) return $from.before(d);
  }
  return null;
}

export function TableToolbar({ editor, canEdit }: Props) {
  // Re-render on every transaction AND on focus/blur — "are we in a table"
  // is selection state, and "should we even be showing" is focus state
  // (clicking a docked palette blurs the editor, which must hide this).
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    editor.on('transaction', force);
    editor.on('focus', force);
    editor.on('blur', force);
    return () => {
      editor.off('transaction', force);
      editor.off('focus', force);
      editor.off('blur', force);
    };
  }, [editor]);

  // The toolbar's own buttons live outside the contenteditable (portalled to
  // <body>) — without this, clicking one would blur the editor on mousedown,
  // BEFORE the click even fires, hiding the toolbar out from under the
  // click. Preventing the default mousedown behavior keeps focus (and the
  // selection) exactly where it was; the click still fires normally.
  const keepFocus = (e: React.MouseEvent) => e.preventDefault();

  const pos = canEdit && editor.isFocused ? tablePos(editor) : null;
  const [rect, setRect] = React.useState<DOMRect | null>(null);

  React.useLayoutEffect(() => {
    if (pos == null) {
      setRect(null);
      return;
    }
    const place = () => {
      const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
      setRect(dom ? dom.getBoundingClientRect() : null);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
    // Re-measure on every transaction too — resizing a column or adding a
    // row moves the table's bottom edge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, editor.state]);

  if (pos == null || !rect) return null;

  const tableNode = editor.state.doc.nodeAt(pos);
  const numbered = !!tableNode?.attrs.numbered;

  const run = (fn: () => boolean) => () => {
    fn();
    editor.commands.focus();
  };
  const align = (v: 'left' | 'center' | 'right') =>
    run(() => editor.commands.setCellAttribute('align', v));
  const toggleNumbered = () =>
    run(() => {
      const at = tablePos(editor);
      if (at == null) return false;
      editor.view.dispatch(editor.state.tr.setNodeAttribute(at, 'numbered', !numbered));
      return true;
    })();

  // Viewport-clamped (DESIGN §4): a generous fixed estimate of the bar's own
  // width keeps it from running off the right edge without a second
  // measure-then-place render pass.
  const ESTIMATED_W = 480;
  const TOOLBAR_H = 40;
  const top = Math.max(8, rect.top - TOOLBAR_H - 8);
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - ESTIMATED_W - 8);

  return createPortal(
    <div
      className="dsg-table-tb"
      role="toolbar"
      aria-label="Table"
      onMouseDown={keepFocus}
      style={{ top, left }}
    >
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.addRowBefore())}
        disabled={!canEdit}
        aria-label="Insert row above"
        title="Insert row above"
      >
        <ArrowUpToLine size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.addRowAfter())}
        disabled={!canEdit}
        aria-label="Insert row below"
        title="Insert row below"
      >
        <ArrowDownToLine size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.deleteRow())}
        disabled={!canEdit}
        aria-label="Delete row"
        title="Delete row"
      >
        <Rows3 size={15} aria-hidden="true" />
      </button>

      <div className="dsg-table-tb-sep" role="separator" />

      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.addColumnBefore())}
        disabled={!canEdit}
        aria-label="Insert column left"
        title="Insert column left"
      >
        <ArrowLeftToLine size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.addColumnAfter())}
        disabled={!canEdit}
        aria-label="Insert column right"
        title="Insert column right"
      >
        <ArrowRightToLine size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.deleteColumn())}
        disabled={!canEdit}
        aria-label="Delete column"
        title="Delete column"
      >
        <Columns3 size={15} aria-hidden="true" />
      </button>

      <div className="dsg-table-tb-sep" role="separator" />

      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.mergeCells())}
        disabled={!canEdit}
        aria-label="Merge selected cells"
        title="Merge selected cells"
      >
        <TableCellsMerge size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.splitCell())}
        disabled={!canEdit}
        aria-label="Split cell"
        title="Split cell"
      >
        <TableCellsSplit size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`dsg-icon-btn${editor.isActive('tableHeader') ? ' dsg-tool-on' : ''}`}
        onClick={run(() => editor.commands.toggleHeaderRow())}
        disabled={!canEdit}
        aria-label="Toggle header row (repeats on every printed page)"
        title="Toggle header row (repeats on every printed page)"
        aria-pressed={editor.isActive('tableHeader')}
      >
        <PanelTop size={15} aria-hidden="true" />
      </button>

      <div className="dsg-table-tb-sep" role="separator" />

      <button
        type="button"
        className="dsg-icon-btn"
        onClick={align('left')}
        disabled={!canEdit}
        aria-label="Align cell text left"
        title="Align cell text left"
      >
        <AlignLeft size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={align('center')}
        disabled={!canEdit}
        aria-label="Align cell text center"
        title="Align cell text center"
      >
        <AlignCenter size={15} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="dsg-icon-btn"
        onClick={align('right')}
        disabled={!canEdit}
        aria-label="Align cell text right"
        title="Align cell text right"
      >
        <AlignRight size={15} aria-hidden="true" />
      </button>

      <div className="dsg-table-tb-sep" role="separator" />

      <button
        type="button"
        className={`dsg-table-tb-toggle${numbered ? ' dsg-tool-on' : ''}`}
        onClick={toggleNumbered}
        disabled={!canEdit}
        aria-pressed={numbered}
        title={
          numbered
            ? 'Numbered under this level — click to make it standalone'
            : 'Standalone (no number) — click to number it under this level'
        }
      >
        {numbered ? 'Numbered' : 'Standalone'}
      </button>

      <div className="dsg-table-tb-sep" role="separator" />

      <button
        type="button"
        className="dsg-icon-btn"
        onClick={run(() => editor.commands.deleteTable())}
        disabled={!canEdit}
        aria-label="Delete table"
        title="Delete table — Ctrl+Z brings it back"
      >
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </div>,
    document.body
  );
}
